/**
 * CheckpointComparisonTable
 *
 * Standalone checkpoint timeline table for reuse in Race Dynamics tab and
 * Summary tab.
 *
 * Shows one row per checkpoint: name, course km, services, cutoff, A arrival,
 * A stop, B arrival, B stop, gap at checkpoint, and which runner won that section.
 */

import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  useTheme,
} from '@mui/material';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import RamenDiningIcon from '@mui/icons-material/RamenDining';
import LuggageIcon from '@mui/icons-material/Luggage';
import HotelIcon from '@mui/icons-material/Hotel';
import GroupIcon from '@mui/icons-material/Group';
import TimerIcon from '@mui/icons-material/Timer';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const abs = Math.abs(seconds);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = Math.floor(abs % 60);
  const sign = seconds < 0 ? '−' : '';
  if (h > 0) return `${sign}${h}h ${m.toString().padStart(2, '0')}m`;
  return `${sign}${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatKm(m) {
  if (m == null) return '—';
  return `${(m / 1000).toFixed(1)} km`;
}

function ServicesCell({ raceCheckpoint, colors }) {
  const { t } = useTranslation();
  if (!raceCheckpoint) return <Typography variant="caption" color={colors.grey[600]}>—</Typography>;
  const services = raceCheckpoint.services?.toLowerCase() || '';
  const isFood = services.includes('food');
  const isDrinks = services.includes('drinks');
  const hasCrew = raceCheckpoint.assistance_permitted;
  const hasWarmMeal = raceCheckpoint.warm_meal === true;
  const hasSpareBag = raceCheckpoint.spare_bag === true;
  const hasRestArea = raceCheckpoint.rest_area === true;
  const hasAny = isFood || isDrinks || hasCrew || hasWarmMeal || hasSpareBag || hasRestArea;
  return (
    <Box display="flex" gap={0.5} alignItems="center" justifyContent="center">
      {isFood && <LocalDiningIcon sx={{ fontSize: 16, color: '#4ade80' }} titleAccess={t('foodIcon', 'Food')} />}
      {isDrinks && <LocalCafeIcon sx={{ fontSize: 16, color: '#60a5fa' }} titleAccess={t('drinksIcon', 'Drinks')} />}
      {hasWarmMeal && <RamenDiningIcon sx={{ fontSize: 16, color: '#fb923c' }} titleAccess={t('warmMealIcon', 'Warm meal')} />}
      {hasSpareBag && <LuggageIcon sx={{ fontSize: 16, color: '#93c5fd' }} titleAccess={t('spareBagIcon', 'Spare bag')} />}
      {hasRestArea && <HotelIcon sx={{ fontSize: 16, color: '#86efac' }} titleAccess={t('restAreaIcon', 'Rest area')} />}
      {hasCrew && <GroupIcon sx={{ fontSize: 16, color: '#d1d5db' }} titleAccess={t('crewAccessIcon', 'Crew access')} />}
      {!hasAny && <TimerIcon sx={{ fontSize: 16, color: colors.grey[600] }} titleAccess={t('timingOnlyIcon', 'Timing only')} />}
    </Box>
  );
}

export default function CheckpointComparisonTable({ checkpoints, race }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  if (!checkpoints?.length) return null;

  // Build a lookup map: checkpoint name → race checkpoint data
  const raceCheckpointMap = {};
  (race?.checkpoints || []).forEach((cp) => {
    if (cp.name) raceCheckpointMap[cp.name] = cp;
  });
  const hasRaceInfo = Object.keys(raceCheckpointMap).length > 0;

    const thSx = (color) => ({
    backgroundColor: colors.grey[900],
    color: color || colors.grey[300],
    fontWeight: 'bold',
    fontSize: '0.75rem',
    py: 0.75,
    whiteSpace: 'nowrap',
  });

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={thSx()}>{t('checkpointHeader', 'Checkpoint')}</TableCell>
            {hasRaceInfo && (
              <TableCell align="center" sx={thSx()}>{t('servicesHeader', 'Services')}</TableCell>
            )}
            <TableCell align="center" sx={thSx(COLOR_A)}>{t('aArrivalHeader', 'A Arrival')}</TableCell>
            <TableCell align="center" sx={thSx(COLOR_A)}>{t('aStopHeader', 'A Stop')}</TableCell>
            <TableCell align="center" sx={thSx(COLOR_B)}>{t('bArrivalHeader', 'B Arrival')}</TableCell>
            <TableCell align="center" sx={thSx(COLOR_B)}>{t('bStopHeader', 'B Stop')}</TableCell>
            <TableCell align="center" sx={thSx()}>{t('gapHeader', 'Gap')}</TableCell>
            <TableCell align="center" sx={thSx()}>{t('sectionHeader', 'Section')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {checkpoints.map((cp, i) => {
            const raceCP = raceCheckpointMap[cp.name] || null;
            const gapColor =
              cp.gap_at_cp_s == null ? colors.grey[500]
              : cp.gap_at_cp_s < 0 ? COLOR_A
              : COLOR_B;
            const sectionWinner =
              cp.section_a && cp.section_b
                ? (cp.section_a.section_moving_s ?? 0) < (cp.section_b.section_moving_s ?? 0)
                  ? 'A'
                  : 'B'
                : null;

            return (
              <TableRow
                key={i}
                sx={{ '&:nth-of-type(even)': { backgroundColor: colors.grey[800] } }}
              >
                <TableCell>
                  <Typography variant="caption" fontWeight="bold" color={colors.grey[200]}>
                    {cp.name || `CP ${i + 1}`}
                  </Typography>
                  {cp.distance_m != null && (
                    <Typography variant="caption" color={colors.grey[500]} display="block">
                      {formatKm(cp.distance_m)}
                    </Typography>
                  )}
                </TableCell>

                {hasRaceInfo && (
                  <TableCell align="center">
                    <ServicesCell raceCheckpoint={raceCP} colors={colors} />
                  </TableCell>
                )}

                <TableCell align="center" sx={{ color: COLOR_A, fontSize: '0.8rem' }}>
                  {cp.arrived_a_s != null ? formatDuration(cp.arrived_a_s) : '—'}
                </TableCell>
                <TableCell align="center" sx={{ color: (cp.stop_a_s > 600) ? '#ef4444' : colors.grey[300], fontSize: '0.8rem' }}>
                  {cp.stop_a_s > 10 ? formatDuration(cp.stop_a_s) : '—'}
                </TableCell>
                <TableCell align="center" sx={{ color: COLOR_B, fontSize: '0.8rem' }}>
                  {cp.arrived_b_s != null
                    ? formatDuration(cp.arrived_b_s)
                    : cp.b_detected === false
                    ? t('notDetected', '(not detected)')
                    : '—'}
                </TableCell>
                <TableCell align="center" sx={{ color: (cp.stop_b_s > 600) ? '#ef4444' : colors.grey[300], fontSize: '0.8rem' }}>
                  {cp.stop_b_s > 10 ? formatDuration(cp.stop_b_s) : '—'}
                </TableCell>
                <TableCell align="center" sx={{ color: gapColor, fontWeight: 'bold', fontSize: '0.8rem' }}>
                  {cp.gap_at_cp_s != null ? formatDuration(cp.gap_at_cp_s) : '—'}
                </TableCell>
                <TableCell align="center">
                  {sectionWinner ? (
                    <Chip
                      label={sectionWinner}
                      size="small"
                      sx={{
                        backgroundColor: sectionWinner === 'A' ? COLOR_A : COLOR_B,
                        color: '#fff',
                        fontWeight: 'bold',
                        height: 20,
                        fontSize: '0.7rem',
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color={colors.grey[600]}>—</Typography>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
